import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';

import PageLoader from 'components/PageLoader';
import { useQuery } from '@tanstack/react-query';
import useDocumentTitle from 'hooks/useDocumentTitle';
import TabbedPageHeader from 'components/TabbedPageHeader';
import {
  getCalibrations,
  CalibrationItem,
  getCalibrationsHistory
} from 'actions/calibrations';
import CalibrationCard from 'components/Calibrations/CalibrationCard';
import CalibrationHistoryCard from 'components/Calibrations/CalibrationHistoryCard';
import { TS_PURCHASE_CALIBRATION_URL } from 'constants/environmentVariables';

function Calibrations({
  isCalibrationsHistory = false
}: {
  isCalibrationsHistory?: boolean;
}) {
  useDocumentTitle(
    i18n.ft(isCalibrationsHistory ? messages.history : messages.current)
  );

  const { data: calibrationsResponse, isLoading: isCalibrationsLoading } =
    useQuery({
      queryKey: ['calibrations', isCalibrationsHistory],
      queryFn: () =>
        isCalibrationsHistory ? getCalibrationsHistory() : getCalibrations()
    });

  function userHasCalibrations() {
    return (
      calibrationsResponse &&
      calibrationsResponse.data &&
      calibrationsResponse.data.calibrations &&
      calibrationsResponse.data.calibrations.length > 0
    );
  }

  function displayCalibrationsResponse() {
    if (isCalibrationsLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      );
    }

    if (!userHasCalibrations()) {
      return (
        <div className="flex justify-center items-center py-12">
          <p className="text-gray-600">{i18n.ft(messages.noData)}</p>
        </div>
      );
    }

    return calibrationsResponse?.data?.calibrations?.map(
      (calibration: CalibrationItem) =>
        isCalibrationsHistory ? (
          <CalibrationHistoryCard
            calibration={calibration}
            key={calibration.id}
          />
        ) : (
          <CalibrationCard calibration={calibration} key={calibration.id} />
        )
    );
  }

  const tabs = [
    {
      to: RouteHelpers.getPath('calibrations-view'),
      label: i18n.ft(messages.current),
      end: true
    },
    {
      to: RouteHelpers.getPath('calibrations-history'),
      label: i18n.ft(messages.history)
    }
  ];

  if (isCalibrationsLoading) {
    return <PageLoader />;
  }

  return (
    <main className="max-w-[1360px] mx-auto my-10 px-4 2xl:px-0 min-h-[calc(100vh-420px)]">
      <div className="flex flex-col-reverse gap-5 mb-5 md:flex-row md:justify-between md:items-start md:gap-0 md:mb-8">
        <TabbedPageHeader
          title={i18n.ft(messages.title)}
          tabs={tabs}
          className="flex-1"
        />
        <button
          className="self-start shrink-0 text-gray-600 hover:text-gray-800 flex items-center gap-1 bg-[#EAEAEA] rounded-full p-2 font-bold"
          onClick={() => {
            window.open(
              TS_PURCHASE_CALIBRATION_URL,
              '_blank',
              'noopener,noreferrer'
            );
          }}
        >
          {i18n.ft(messages.purchase)}
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </button>
      </div>

      <div
        className={
          isCalibrationsHistory ? 'flex flex-col gap-3 md:gap-2' : 'space-y-6'
        }
      >
        {displayCalibrationsResponse()}
      </div>
    </main>
  );
}

export default Calibrations;
